import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  type SelectOption,
  type StringIndexedObject,
  type UseFormReturn,
  useForm,
  useWatch,
} from '@kanda-libs/ks-component-ts';
import { useToast } from '@kanda-libs/ks-design-library';
import {
  type Company,
  type FinanceProvider,
  type FinanceRate,
  type Rate,
  actions,
} from '@kanda-libs/ks-frontend-services';

import { useAppDispatch } from 'components/shared/App';
import useReloadInitialData from 'hooks/useReloadInitialData';
import companySelectors from 'store/selectors/company';

import {
  type CustomRatesFormValues,
  type RateFormRow,
  buildRateRows,
  formatRateBody,
  getExistingXid,
  getProviderOptions,
  requiresUserId,
  upsertLenderConfig,
} from '../../helpers';

export interface CustomRatesFormArgs {
  allRates: FinanceRate[];
  existing: FinanceRate[] | null;
  hasExisting: boolean;
  providersRequiringUserId: FinanceProvider[];
}

export interface CustomRatesFormHook {
  form: UseFormReturn<CustomRatesFormValues>;
  step: number;
  provider: string;
  providerOptions: SelectOption[];
  rows: RateFormRow[];
  showUserId: boolean;
  goNext: () => void;
  goBack: () => void;
  onSubmit: (values: StringIndexedObject) => void;
  isSubmitting: boolean;
}

export default function useCustomRatesForm(
  handleClose: () => void,
  {
    allRates,
    existing,
    hasExisting,
    providersRequiringUserId,
  }: CustomRatesFormArgs,
): CustomRatesFormHook {
  const dispatch = useAppDispatch();
  const reloadInitialData = useReloadInitialData();
  const { showSuccess, showError } = useToast();

  const company = useSelector(companySelectors.getEntity);

  const existingRates = useMemo(() => existing || [], [existing]);

  const providerOptions = useMemo(
    () => getProviderOptions(allRates),
    [allRates],
  );

  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<CustomRatesFormValues>({
    mode: 'onBlur',
    defaultValues: { provider: undefined, rates: [], xid: '' },
  });
  const { control, setValue } = form;

  const provider = useWatch({ control, name: 'provider' });
  const rows = (useWatch({ control, name: 'rates' }) as RateFormRow[]) || [];

  console.log({ provider, rows, allRates, existingRates });

  const showUserId = useMemo(
    () => requiresUserId(provider, providersRequiringUserId),
    [provider, providersRequiringUserId],
  );

  // Preselect the lender from the company's existing custom table (a company
  // works with a single lender), so re-opening lands on that lender's rates.
  useEffect(() => {
    const existingProvider = existingRates[0]?.provider;
    const lenderInRates = existingProvider
      ? allRates.map((r) => r.provider).includes(existingProvider)
      : false;
    if (!lenderInRates) return;
    if (existingProvider) setValue('provider', existingProvider);
  }, [existingRates, allRates, setValue]);

  // Rebuild the rate rows whenever the lender (or the source data) changes,
  // merging in any existing custom selections/fees for that lender, and prefill
  // the lender's stored user id (xid) if one already exists.
  useEffect(() => {
    setValue('rates', buildRateRows(allRates, provider, existingRates));
    setValue('xid', getExistingXid(company?.lender_configs, provider));
  }, [provider, allRates, existingRates, company?.lender_configs, setValue]);

  const goNext = useCallback(() => {
    if (provider) setStep(1);
  }, [provider]);

  const goBack = useCallback(() => setStep(0), []);

  const onSubmit = useCallback(
    (values: StringIndexedObject) => {
      const { rates, xid } = values as CustomRatesFormValues;
      const body = formatRateBody(rates);
      if (body.finance_rates.length === 0) {
        showError('Select at least one rate');
        return;
      }
      const id = company?.cid;
      if (!id) {
        showError('Company ID not found');
        return;
      }
      const needsUserId = requiresUserId(provider, providersRequiringUserId);
      const trimmedXid = (xid || '').trim();
      if (needsUserId && !trimmedXid) {
        showError('This lender requires a user id');
        return;
      }

      setIsSubmitting(true);

      const finish = () => {
        setIsSubmitting(false);
        showSuccess('Rates updated');
        reloadInitialData();
        handleClose();
      };

      // Persist the lender user id onto the company's lender_configs (upsert by
      // provider) after the rate table saves. Nothing to persist otherwise.
      const persistLenderConfig = () => {
        if (!needsUserId || !company) {
          finish();
          return;
        }
        dispatch(
          actions.putCompany({
            body: {
              ...company,
              lender_configs: upsertLenderConfig(
                company.lender_configs,
                provider as FinanceProvider,
                trimmedXid,
              ),
            } as Company,
            params: { id },
            onSuccess: finish,
            onError: () => {
              setIsSubmitting(false);
              showError('Failed to save lender user id');
            },
          }),
        );
      };

      const action = hasExisting
        ? actions.putCompanyRate
        : actions.postCompanyRate;
      dispatch(
        action({
          body: body as Rate,
          params: { id },
          onSuccess: persistLenderConfig,
          onError: () => {
            setIsSubmitting(false);
            showError('Failed to update rates');
          },
        }),
      );
    },
    [
      company,
      provider,
      providersRequiringUserId,
      hasExisting,
      dispatch,
      showSuccess,
      showError,
      reloadInitialData,
      handleClose,
    ],
  );

  return {
    form,
    step,
    provider,
    providerOptions,
    rows,
    showUserId,
    goNext,
    goBack,
    onSubmit,
    isSubmitting,
  };
}
